import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Copy, Trash2, Link2 } from 'lucide-react';
import { gigSwal, escapeHtml } from '../utils/swal';
import { useBandRole } from '../context/BandContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const ROLE_INFO = {
  owner: { label: 'Owner', desc: 'Everything, including who can sign in' },
  admin: { label: 'Admin', desc: 'Songs, gigs, set lists, settings — not logins' },
  performer: { label: 'Performer', desc: 'Sees songs, lyrics, chords and set lists; marks requests played' },
};

const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// "Logins & access" tab of Branding & Settings (owners only): who can sign in to this band,
// with what role, and single-use invite links for adding people.
export default function LoginsAccess({ theme, bandName }) {
  const { tier } = useBandRole();
  // Inviting is a Band-plan feature (the server enforces it too). Existing members keep their
  // access if a band moves to a lower plan; revisit that (spec §6 question 2).
  const canInvite = tier === 'band';
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteRole, setInviteRole] = useState('performer');
  const [inviteLabel, setInviteLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState(null);   // { url, label, role }

  const load = useCallback(async () => {
    try {
      const [m, i] = await Promise.all([
        axios.get(`${API_URL}/api/members`),
        axios.get(`${API_URL}/api/invites`),
      ]);
      setMembers(m.data.members);
      setMe(m.data.me);
      setInvites(i.data);
    } catch {
      toast.error('Could not load logins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/invites`, { role: inviteRole, label: inviteLabel });
      setNewLink({
        url: `${window.location.origin}/join#${res.data.token}`,
        label: res.data.label,
        role: res.data.role,
      });
      setInviteLabel('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error === 'upgrade_required'
        ? 'Inviting band members needs the Band plan'
        : err.response?.data?.error || 'Could not create the invite');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(newLink.url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — select the link and copy it by hand');
    }
  };

  const whatsappUrl = (url) => `https://wa.me/?text=${encodeURIComponent(
    `You're invited to join ${bandName || 'our band'} on GigRequest: ${url}`)}`;

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    try {
      await axios.patch(`${API_URL}/api/members/${encodeURIComponent(member.user_id)}`, { role });
      toast.success(`${member.name} is now ${ROLE_INFO[role].label}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not change the role');
    }
  };

  const removeMember = async (member) => {
    const result = await gigSwal(theme, {
      title: `Remove ${escapeHtml(member.name)}?`,
      text: "They'll lose access straight away. You can invite them again later.",
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545',
      confirmButtonText: 'Remove', reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_URL}/api/members/${encodeURIComponent(member.user_id)}`);
      toast.success(`${member.name} removed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove them');
    }
  };

  const revokeInvite = async (invite) => {
    try {
      await axios.delete(`${API_URL}/api/invites/${invite.id}`);
      toast.success('Invite revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not revoke the invite');
    }
  };

  const card = { background: theme.surfaceAlt, borderRadius: '10px', padding: '1.25rem' };
  const heading = { margin: '0 0 1rem', fontWeight: '700', fontSize: '0.85rem', color: theme.text, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const input = { width: '100%', padding: '0.65rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box', background: theme.surface, color: theme.text };
  const row = { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}`, flexWrap: 'wrap' };
  const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer' };

  if (loading) return <p style={{ color: theme.textMuted }}>Loading…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.9rem', lineHeight: 1.5 }}>
        The people who can sign in to {bandName || 'your band'}, each with their own email and password.
        This is separate from the <strong>Band Members</strong> tab, which is the public "Meet the Band" page.
      </p>

      {/* Members */}
      <div style={card}>
        <p style={heading}>Who can sign in</p>
        {members.map(m => (
          <div key={m.user_id} style={row}>
            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: theme.text }}>
                {m.name}{m.user_id === me && <span style={{ fontWeight: 400, color: theme.textMuted }}> (you)</span>}
              </div>
              <div style={{ fontSize: '0.82rem', color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email}</div>
            </div>
            <select
              value={m.role}
              onChange={e => changeRole(m, e.target.value)}
              aria-label={`Role for ${m.name}`}
              style={{ ...input, width: 'auto', padding: '0.4rem 0.6rem', fontSize: '0.88rem' }}
            >
              {Object.entries(ROLE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {m.user_id !== me && (
              <button onClick={() => removeMember(m)} style={{ ...smallBtn, background: theme.surface, color: '#dc3545', border: '1px solid #dc3545' }}>
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
        ))}
        <div style={{ marginTop: '0.9rem', fontSize: '0.82rem', color: theme.textMuted, lineHeight: 1.6 }}>
          {Object.values(ROLE_INFO).map(r => <div key={r.label}><strong>{r.label}:</strong> {r.desc}</div>)}
        </div>
      </div>

      {/* Invite */}
      <div style={card}>
        <p style={heading}>Invite someone</p>
        {!canInvite ? (
          <p style={{ margin: 0, color: theme.textMuted, fontSize: '0.9rem', lineHeight: 1.5 }}>
            Inviting band members, so each person has their own login and role, is part of the <strong>Band</strong> plan.
          </p>
        ) : (<>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <input
            style={{ ...input, flex: '1 1 160px', width: 'auto' }}
            value={inviteLabel}
            maxLength={60}
            onChange={e => setInviteLabel(e.target.value)}
            placeholder="Who's it for? e.g. Poppy"
          />
          <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ ...input, width: 'auto' }} aria-label="Invite role">
            <option value="performer">Performer</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={createInvite} disabled={creating} style={{ ...smallBtn, padding: '0.65rem 1rem', background: theme.primary, color: theme.onPrimary, border: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
            <Link2 size={15} /> {creating ? 'Creating…' : 'Create invite link'}
          </button>
        </div>

        {newLink && (
          <div style={{ marginTop: '1rem', padding: '1rem', border: `1px solid ${theme.primary}`, borderRadius: '8px', background: theme.surface }}>
            <div style={{ fontSize: '0.85rem', color: theme.text, marginBottom: '0.5rem' }}>
              Invite link{newLink.label ? ` for ${newLink.label}` : ''} ({ROLE_INFO[newLink.role].label}).
              Send it to them now: it's only shown once, works once, and expires in 7 days.
            </div>
            <input readOnly value={newLink.url} onFocus={e => e.target.select()} style={{ ...input, fontSize: '0.8rem', marginBottom: '0.6rem' }} />
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button onClick={copyLink} style={{ ...smallBtn, background: theme.primary, color: theme.onPrimary, border: 'none' }}>
                <Copy size={14} /> Copy link
              </button>
              <a href={whatsappUrl(newLink.url)} target="_blank" rel="noopener noreferrer" style={{ ...smallBtn, textDecoration: 'none', background: '#25D366', color: '#fff', border: 'none' }}>
                Share on WhatsApp
              </a>
              <button onClick={() => setNewLink(null)} style={{ ...smallBtn, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}` }}>
                Done
              </button>
            </div>
          </div>
        )}

        {invites.length > 0 && (
          <div style={{ marginTop: '1.25rem' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.textMuted, marginBottom: '0.25rem' }}>Waiting to be used</div>
            {invites.map(i => (
              <div key={i.id} style={row}>
                <div style={{ flex: 1, color: theme.text, fontSize: '0.9rem' }}>
                  {i.label || 'Unnamed invite'} · {ROLE_INFO[i.role].label}
                  <span style={{ color: theme.textMuted }}> · expires {formatDate(i.expires_at)}</span>
                </div>
                <button onClick={() => revokeInvite(i)} style={{ ...smallBtn, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}` }}>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
